import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Grid, Paper, TextField, Typography } from '@mui/material';
import { apiService } from '@/services/api';
import { useMessage } from '@/contexts/MessageContext';
import { useRefresh } from '@/contexts/RefreshContext';
import { logger } from '@/utils/logger';
import { CEPApiUnavailableError, cepService } from '@/services/cepService';

type FormState = {
  cnpj: string;
  nome: string;
  endereco: string;
  bairro: string;
  cidade: string;
  uf: string;
  cep: string;
  pixKey: string;
  pixExpirationSeconds: string;
  /** Boleto Inter v3 — campo numDiasAgenda (inteiro positivo). */
  interNumDiasAgenda: string;
};

const emptyState: FormState = {
  cnpj: '',
  nome: '',
  endereco: '',
  bairro: '',
  cidade: '',
  uf: '',
  cep: '',
  pixKey: '',
  pixExpirationSeconds: '',
  interNumDiasAgenda: ''
};

const normalizeDigits = (v: string) => (v || '').replace(/\D/g, '');
const normalizeUf = (v: string) => (v || '').trim().toUpperCase().slice(0, 2);

function maskCnpjFromDigits(digits: string): string {
  const d = normalizeDigits(digits).slice(0, 14);
  const p1 = d.slice(0, 2);
  const p2 = d.slice(2, 5);
  const p3 = d.slice(5, 8);
  const p4 = d.slice(8, 12);
  const p5 = d.slice(12, 14);
  let out = p1;
  if (p2) out += `.${p2}`;
  if (p3) out += `.${p3}`;
  if (p4) out += `/${p4}`;
  if (p5) out += `-${p5}`;
  return out;
}

function maskCepFromDigits(digits: string): string {
  const d = normalizeDigits(digits).slice(0, 8);
  if (d.length <= 5) return d;
  return `${d.slice(0, 5)}-${d.slice(5)}`;
}

type InterBeneficiaryConfigPageProps = {
  /** Quando true, omite o título principal (uso dentro de Integração bancária). */
  embedded?: boolean;
};

const InterBeneficiaryConfigPage: React.FC<InterBeneficiaryConfigPageProps> = ({ embedded }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);
  const [state, setState] = useState<FormState>(emptyState);
  const { showSuccess, showError } = useMessage();
  const { setRefreshCallback } = useRefresh();

  const missing = useMemo(() => {
    const m: string[] = [];
    if (!normalizeDigits(state.cnpj)) m.push('CNPJ');
    if (!state.nome.trim()) m.push('Nome/Razão social');
    if (!state.endereco.trim()) m.push('Endereço');
    if (!state.bairro.trim()) m.push('Bairro');
    if (!state.cidade.trim()) m.push('Cidade');
    if (!normalizeUf(state.uf)) m.push('UF');
    if (!normalizeDigits(state.cep)) m.push('CEP');
    return m;
  }, [state]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.getBillingGlobalConfig();
      const cfg = (res.data || {}) as any;
      const b = cfg.interBeneficiaryFinal || {};
      setState({
        cnpj: b.cnpj || '',
        nome: b.nome || '',
        endereco: b.endereco || '',
        bairro: b.bairro || '',
        cidade: b.cidade || '',
        uf: b.uf || '',
        cep: b.cep || '',
        pixKey: cfg.interPixKey || '',
        pixExpirationSeconds:
          cfg.interPixExpirationSeconds != null ? String(cfg.interPixExpirationSeconds) : '',
        interNumDiasAgenda:
          cfg.interNumDiasAgenda != null && cfg.interNumDiasAgenda !== ''
            ? String(cfg.interNumDiasAgenda)
            : ''
      });
    } catch (e) {
      logger.error('Error loading billing config (Inter beneficiary):', e);
      showError('Falha ao carregar configurações do Billing (Beneficiário).');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setRefreshCallback(load);
    return () => setRefreshCallback(null);
  }, [setRefreshCallback, load]);

  const save = async () => {
    setSaving(true);
    try {
      const pixExpirationSeconds = normalizeDigits(state.pixExpirationSeconds);
      const diasAgendaDigits = normalizeDigits(state.interNumDiasAgenda);
      const payload = {
        interBeneficiaryFinal: {
          cnpj: normalizeDigits(state.cnpj),
          nome: state.nome.trim(),
          endereco: state.endereco.trim(),
          bairro: state.bairro.trim(),
          cidade: state.cidade.trim(),
          uf: normalizeUf(state.uf),
          cep: normalizeDigits(state.cep)
        },
        interPixKey: state.pixKey.trim() || undefined,
        interPixExpirationSeconds: pixExpirationSeconds ? Number(pixExpirationSeconds) : undefined,
        interNumDiasAgenda: diasAgendaDigits ? Number(diasAgendaDigits) : undefined
      };
      const res = await apiService.updateBillingGlobalConfig(payload);
      setState({
        cnpj: res.data?.interBeneficiaryFinal?.cnpj ?? payload.interBeneficiaryFinal.cnpj,
        nome: res.data?.interBeneficiaryFinal?.nome ?? payload.interBeneficiaryFinal.nome,
        endereco:
          res.data?.interBeneficiaryFinal?.endereco ?? payload.interBeneficiaryFinal.endereco,
        bairro: res.data?.interBeneficiaryFinal?.bairro ?? payload.interBeneficiaryFinal.bairro,
        cidade: res.data?.interBeneficiaryFinal?.cidade ?? payload.interBeneficiaryFinal.cidade,
        uf: res.data?.interBeneficiaryFinal?.uf ?? payload.interBeneficiaryFinal.uf,
        cep: res.data?.interBeneficiaryFinal?.cep ?? payload.interBeneficiaryFinal.cep,
        pixKey: res.data?.interPixKey ?? (payload as any).interPixKey ?? '',
        pixExpirationSeconds:
          res.data?.interPixExpirationSeconds != null
            ? String(res.data.interPixExpirationSeconds)
            : (payload as any).interPixExpirationSeconds != null
              ? String((payload as any).interPixExpirationSeconds)
              : '',
        interNumDiasAgenda:
          res.data?.interNumDiasAgenda != null
            ? String(res.data.interNumDiasAgenda)
            : (payload as any).interNumDiasAgenda != null
              ? String((payload as any).interNumDiasAgenda)
              : ''
      });
      showSuccess('Beneficiário salvo com sucesso!');
    } catch (e: any) {
      const msg =
        e?.response?.data?.message || e?.response?.data?.detail || e?.message || 'Falha ao salvar';
      logger.error('Error saving Inter beneficiary config:', e);
      showError(String(msg));
    } finally {
      setSaving(false);
    }
  };

  const lookupCep = async () => {
    const cepDigits = normalizeDigits(state.cep);
    if (!cepService.validarCEP(cepDigits)) return;
    setCepLoading(true);
    try {
      const data = await cepService.buscarCEP(cepDigits);
      setState(s => ({
        ...s,
        cep: normalizeDigits(data.cep),
        endereco: s.endereco?.trim() ? s.endereco : data.logradouro || '',
        bairro: s.bairro?.trim() ? s.bairro : data.bairro || '',
        cidade: s.cidade?.trim() ? s.cidade : data.localidade || '',
        uf: s.uf?.trim() ? s.uf : data.uf || ''
      }));
    } catch (e: any) {
      if (e instanceof CEPApiUnavailableError) {
        showError('ViaCEP indisponível. Preencha o endereço manualmente.');
      } else {
        showError(e?.message || 'Falha ao buscar CEP');
      }
      logger.error('CEP lookup failed (Beneficiário):', e);
    } finally {
      setCepLoading(false);
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ mb: 2 }}>
        {!embedded && <Typography variant="h6">Beneficiário (Receitas)</Typography>}
        <Typography variant="body2" color="text.secondary">
          Esses dados são usados pelo vover-billing para emitir cobranças (PIX/BOLETO).
        </Typography>
      </Box>

      {missing.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Campos obrigatórios faltando: {missing.join(', ')}.
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="CNPJ"
            value={maskCnpjFromDigits(state.cnpj)}
            onChange={e =>
              setState(s => ({ ...s, cnpj: normalizeDigits(e.target.value).slice(0, 14) }))
            }
            placeholder="00.000.000/0000-00"
            disabled={loading || saving}
          />
        </Grid>
        <Grid item xs={12} md={8}>
          <TextField
            fullWidth
            label="Nome / Razão social"
            value={state.nome}
            onChange={e => setState(s => ({ ...s, nome: e.target.value }))}
            disabled={loading || saving}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="CEP"
            value={maskCepFromDigits(state.cep)}
            onChange={e =>
              setState(s => ({ ...s, cep: normalizeDigits(e.target.value).slice(0, 8) }))
            }
            onBlur={() => void lookupCep()}
            placeholder="00000-000"
            disabled={loading || saving}
            helperText={cepLoading ? 'Buscando CEP...' : undefined}
          />
        </Grid>
        <Grid item xs={12} md={8}>
          <TextField
            fullWidth
            label="Endereço"
            value={state.endereco}
            onChange={e => setState(s => ({ ...s, endereco: e.target.value }))}
            placeholder="Rua, número"
            disabled={loading || saving}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Bairro"
            value={state.bairro}
            onChange={e => setState(s => ({ ...s, bairro: e.target.value }))}
            disabled={loading || saving}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Cidade"
            value={state.cidade}
            onChange={e => setState(s => ({ ...s, cidade: e.target.value }))}
            disabled={loading || saving}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField
            fullWidth
            label="UF"
            value={state.uf}
            onChange={e => setState(s => ({ ...s, uf: e.target.value }))}
            inputProps={{ maxLength: 2 }}
            disabled={loading || saving}
          />
        </Grid>
        <Grid item xs={12} md={8}>
          <TextField
            fullWidth
            label="Chave PIX (para cobrança imediata)"
            value={state.pixKey}
            onChange={e => setState(s => ({ ...s, pixKey: e.target.value }))}
            placeholder="EVP / CNPJ / e-mail / telefone / chave aleatória"
            disabled={loading || saving}
            helperText="Usada para emitir PIX imediato via API Pix."
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Expiração do PIX (segundos) — opcional"
            value={state.pixExpirationSeconds}
            onChange={e => setState(s => ({ ...s, pixExpirationSeconds: e.target.value }))}
            placeholder="86400"
            disabled={loading || saving}
            helperText="Se vazio, o banco aplica o padrão do Pix (geralmente 24h)."
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Dias de agenda (boleto)"
            value={state.interNumDiasAgenda}
            onChange={e => setState(s => ({ ...s, interNumDiasAgenda: e.target.value }))}
            placeholder="60"
            disabled={loading || saving}
            helperText="Obrigatório para boleto no Inter (API v3), salvo aqui ou via INTER_NUM_DIAS_AGENDA no billing."
          />
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        <Button
          variant="contained"
          onClick={save}
          disabled={saving || loading || missing.length > 0}
        >
          {saving ? 'Salvando...' : 'Salvar'}
        </Button>
      </Box>
    </Paper>
  );
};

export default InterBeneficiaryConfigPage;
